import { useCallback, useEffect, useState } from "react";
import { Check, ExternalLink, Eye, Loader2, MessageCircle, PackageCheck, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Badge, EmptyRow, PanelTitle, StatCard, Table, VERIFICATION_LABELS, fmtDateTime, money, shortHash } from "../office/ui";
import { ORDER_STATUS } from "../office/CodeStorePanel";

const FILTERS = [["", "Todos"], ["en_revision", "Por revisar"], ["pagado", "Pagados · por entregar"], ["entregado", "Entregados"], ["pendiente", "Sin pago"], ["rechazado", "Rechazados"]];

const ProofModal = ({ id, onClose }) => {
  const [o, setO] = useState(null);
  useEffect(() => {
    api.get(`/admin/code-orders/${id}`).then(({ data }) => setO(data));
  }, [id]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/70 p-4" onClick={onClose} data-testid="order-proof-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-display text-lg font-semibold">Comprobante · {o?.distributor_name} ({o?.dist_id})</p>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full hover:bg-paper" data-testid="order-proof-close"><X className="h-4 w-4" /></button>
        </div>
        {!o ? <Loader2 className="mt-6 h-5 w-5 animate-spin" /> : o.payment?.proof_image ? <img src={o.payment.proof_image} alt="Comprobante" className="mt-4 w-full rounded-xl border border-ink/10" /> : <p className="mt-4 text-sm text-ink/70">El distribuidor no adjuntó imagen; solo envió el hash.</p>}
        {o?.payment?.note && <p className="mt-3 text-sm">Nota: {o.payment.note}</p>}
      </div>
    </div>
  );
};

export const CodeOrdersPanel = ({ onChanged, goTo }) => {
  const [status, setStatus] = useState("");
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState("");
  const [proofId, setProofId] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/admin/code-orders", { params: { status } }), api.get("/admin/code-orders/summary")]);
      setItems(a.data);
      setSummary(b.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (o, action) => {
    let body = {};
    if (action === "approve" || action === "reject") {
      const note = window.prompt(action === "approve" ? "Nota para el distribuidor (opcional):" : "Motivo del rechazo:", "");
      if (note === null) return;
      body = { note };
    }
    if (action === "deliver") {
      const phone = window.prompt("WhatsApp de entrega (deja vacío para usar el celular del distribuidor):", "");
      if (phone === null) return;
      body = { phone };
    }
    setBusy(o.id + action);
    try {
      const { data } = await api.post(`/admin/code-orders/${o.id}/${action}`, body);
      if (action === "verify") toast[data.status === "pagado" ? "success" : "info"](VERIFICATION_LABELS[data.payment?.verification?.reason] || "Verificado");
      else if (action === "deliver") {
        toast.success(`${data.delivery.codes.length} códigos entregados a ${data.distributor_name}. Ya aparecen en su oficina virtual.`);
        window.open(data.whatsapp_url, "_blank", "noopener");
      } else toast.success(action === "approve" ? "Pago aprobado. Ahora entrega los códigos." : "Pedido rechazado");
      await load();
      onChanged?.();
    } catch (e) {
      const msg = formatApiError(e);
      toast.error(msg);
      if (msg.includes("disponibles")) goTo?.("codigos");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="code-orders-panel">
      <PanelTitle title="Pedidos de códigos" text="Compras de códigos de acceso realizadas por los distribuidores ($20 USD por código). Revisa el comprobante, aprueba el pago y entrega los códigos: se asignan automáticamente del inventario de códigos gratuitos (30 días), aparecen en la oficina virtual del distribuidor y se abre WhatsApp para enviárselos." />
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard dark label="Pagos por revisar" value={summary ? summary.en_revision : "…"} testId="orders-stat-review" />
        <StatCard label="Pagados · por entregar" value={summary ? summary.pagado : "…"} testId="orders-stat-paid" />
        <StatCard label="Entregados" value={summary ? summary.entregado : "…"} testId="orders-stat-delivered" />
        <StatCard label="Ingresos por códigos" value={summary ? money(summary.ingresos_usd) : "…"} testId="orders-stat-revenue" />
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map(([v, l]) => <button key={v} onClick={() => setStatus(v)} className={`rounded-full px-3.5 py-2 text-xs font-semibold ${status === v ? "bg-ink text-white" : "border border-ink/15 bg-white hover:bg-paper"}`} data-testid={`orders-filter-${v || "all"}`}>{l}</button>)}
      </div>
      <Table headers={["Fecha", "Distribuidor", "Códigos comprados", "Total", "Pago", "Estado", ""]} testId="code-orders-table" minWidth="min-w-[1100px]">
        {!items && <tr><td colSpan={7} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
        {items && !items.length && <EmptyRow cols={7} text="No hay pedidos en esta vista." testId="orders-empty" />}
        {items?.map((o, i) => (
          <tr key={o.id} className="border-b border-ink/5 align-top last:border-0" data-testid={`code-order-row-${i}`}>
            <td className="whitespace-nowrap px-4 py-3 text-ink/70">{fmtDateTime(o.created_at)}</td>
            <td className="px-4 py-3"><p className="font-semibold">{o.distributor_name}</p><p className="font-mono text-xs text-ink/60">ID {o.dist_id} · {o.phone}</p></td>
            <td className="px-4 py-3 text-sm">{o.items.map((it) => <p key={it.platform}><span className="font-semibold">{it.platform}</span> × {it.count}</p>)}{o.delivery && <p className="mt-1 font-mono text-[11px] text-leaf-deep">{o.delivery.codes.join(", ")}</p>}</td>
            <td className="px-4 py-3 font-display text-base font-semibold">{money(o.total_usd)}</td>
            <td className="max-w-[240px] px-4 py-3 text-xs text-ink/75">
              {o.payment ? (
                <>
                  {o.payment.token} · <a href={`https://polygonscan.com/tx/${o.payment.tx_hash}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-leaf-deep hover:underline">{shortHash(o.payment.tx_hash)} <ExternalLink className="h-3 w-3" /></a>
                  {o.payment.verification && <p>{VERIFICATION_LABELS[o.payment.verification.reason] || o.payment.verification.reason}{o.payment.verification.amount != null && ` · ${o.payment.verification.amount} ${o.payment.token}`}</p>}
                </>
              ) : "Sin pago aún"}
            </td>
            <td className="px-4 py-3"><Badge status={ORDER_STATUS[o.status][0]} label={ORDER_STATUS[o.status][1]} />{o.review_note && <p className="mt-1 text-xs text-ink/60">“{o.review_note}”</p>}</td>
            <td className="px-4 py-3">
              <div className="flex flex-wrap gap-1.5">
                {o.payment && <button onClick={() => setProofId(o.id)} className="grid h-8 w-8 place-items-center rounded-full border border-ink/15 hover:bg-paper" title="Ver comprobante" data-testid={`order-view-${i}`}><Eye className="h-4 w-4" /></button>}
                {o.status === "en_revision" && (
                  <>
                    <button onClick={() => act(o, "verify")} disabled={!!busy} className="grid h-8 w-8 place-items-center rounded-full border border-ink/15 hover:bg-paper" title="Re-verificar en blockchain" data-testid={`order-verify-${i}`}><RefreshCw className={`h-4 w-4 ${busy === o.id + "verify" ? "animate-spin" : ""}`} /></button>
                    <button onClick={() => act(o, "approve")} disabled={!!busy} className="grid h-8 w-8 place-items-center rounded-full bg-leaf text-ink hover:bg-[#9bd94b]" title="Aprobar pago" data-testid={`order-approve-${i}`}><Check className="h-4 w-4" /></button>
                    <button onClick={() => act(o, "reject")} disabled={!!busy} className="grid h-8 w-8 place-items-center rounded-full bg-coral/10 text-coral hover:bg-coral/20" title="Rechazar" data-testid={`order-reject-${i}`}><X className="h-4 w-4" /></button>
                  </>
                )}
                {o.status === "pagado" && (
                  <button onClick={() => act(o, "deliver")} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-60" data-testid={`order-deliver-${i}`}>
                    {busy === o.id + "deliver" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PackageCheck className="h-3.5 w-3.5" />} Entregar códigos
                  </button>
                )}
                {o.status === "entregado" && o.delivery?.whatsapp_url && (
                  <a href={o.delivery.whatsapp_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold hover:bg-paper" data-testid={`order-resend-${i}`}><MessageCircle className="h-3.5 w-3.5" /> Reenviar WhatsApp</a>
                )}
              </div>
            </td>
          </tr>
        ))}
      </Table>
      {proofId && <ProofModal id={proofId} onClose={() => setProofId("")} />}
    </div>
  );
};
